import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { lastValueFrom } from "rxjs";
import { CacheService } from "./cache.service";
import { FeatureService } from "./features.service";
import { ReactNativeBundleComponent } from "../react-native-bundle/react-native-bundle.component";
import { FeatureUpgradeData } from "../features/ui-notification/feature-upgrade-notification.component";
import { UpgradeModalService } from "./upgrade-modal.service";

interface FeatureVersion {
  version: string;
  baseUrl: string;
  homeName: string;
}

@Injectable({
  providedIn: "root",
})
export class FeatureUpgradeService {
  private readonly UPGRADE_DISMISSED_KEY = "feature-upgrade-dismissed";

  constructor(
    private cacheService: CacheService,
    private featureService: FeatureService,
    private upgradeModalService: UpgradeModalService,
    private router: Router
  ) {}

  /**
   * Compare cached version with remote version for a feature
   * Returns true if remote version is newer
   */
  public async checkForFeatureUpgrade(
    baseUrl: string
  ): Promise<{ hasUpgrade: boolean; data?: FeatureUpgradeData }> {
    const functionStartTime = Date.now();
    console.log(
      `[UPGRADE] ═══════════════════════════════════════════════════════`
    );
    console.log(
      `[UPGRADE] 🟢 checkForFeatureUpgrade STARTED at ${new Date().toISOString()}`
    );
    console.log(`[UPGRADE] Input baseUrl: "${baseUrl}"`);

    try {
      // Trim baseUrl to handle leading/trailing spaces
      const trimmedBaseUrl = baseUrl.trim();
      const featureDescUrl = `${trimmedBaseUrl}feature-description.json`;

      console.log(`[UPGRADE] Trimmed baseUrl: "${trimmedBaseUrl}"`);
      console.log(`[UPGRADE] Feature desc URL: ${featureDescUrl}`);

      // Check if cached version exists
      console.log(`[UPGRADE] Checking if cached version exists...`);
      const cachedExists = await this.cacheService.doesKeyExist(featureDescUrl);
      console.log(`[UPGRADE] doesKeyExist result: ${cachedExists}`);

      if (!cachedExists) {
        console.log(
          `[UPGRADE] ⚠️ No cached version for ${trimmedBaseUrl}, skipping upgrade check`
        );
        console.log(
          `[UPGRADE] 🔚 RETURNING: No cached version exists (duration: ${
            Date.now() - functionStartTime
          }ms)`
        );
        return { hasUpgrade: false };
      }

      // Check for running version first (what's actually loaded in memory)
      console.log(
        `[UPGRADE] 🔍 Looking up running version for baseUrl: "${baseUrl}"`
      );
      console.log(`[UPGRADE] 📋 Current runningVersions map contents:`);
      console.log(
        `[UPGRADE] Map size: ${ReactNativeBundleComponent.runningVersions.size}`
      );
      ReactNativeBundleComponent.runningVersions.forEach((version, key) => {
        console.log(`[UPGRADE]   Key: "${key}" => Version: "${version}"`);
      });

      const runningVersion = ReactNativeBundleComponent.runningVersions.get(
        baseUrl.trim()
      );
      let currentVersion: string;
      let featureName: string = "Feature";

      console.log(`[UPGRADE] running version result: ${runningVersion}`);

      if (!runningVersion) {
        console.log(
          `[UPGRADE] ⚠️ No running version found for key "${baseUrl.trim()}"`
        );
        console.log(
          `[UPGRADE] 💡 Feature launched from cache - checking cached version against remote`
        );

        // Get cached version to compare with remote
        try {
          const cachedItems = await this.cacheService.getCachedItems(
            (key) => key === `resource-cache-${featureDescUrl}`,
            false
          );
          if (cachedItems.length > 0) {
            const cachedData = cachedItems[0]?.data;
            currentVersion = cachedData?.version;
            featureName = cachedData?.homeName || "Feature";
            console.log(
              `[UPGRADE] ✅ Found cached version: ${currentVersion} for ${featureName}`
            );
          } else {
            console.log(
              `[UPGRADE] ❌ No cached descriptor found, cannot check for upgrade`
            );
            return { hasUpgrade: false };
          }
        } catch (err) {
          console.log(`[UPGRADE] ❌ Error reading cached descriptor:`, err);
          return { hasUpgrade: false };
        }
      } else {
        console.log(
          `[UPGRADE] ✅ Found running version in memory: ${runningVersion}`
        );
        currentVersion = runningVersion;

        // Get feature name from cached descriptor for display purposes only
        try {
          const cachedItems = await this.cacheService.getCachedItems(
            (key) => key === `resource-cache-${featureDescUrl}`,
            false
          );
          if (cachedItems.length > 0) {
            featureName = cachedItems[0]?.data?.homeName || "Feature";
          }
        } catch (err) {
          console.log(
            `[UPGRADE] Could not get feature name from cache, using default`
          );
        }
      }

      // Fetch fresh version from remote - WITHOUT caching to avoid sync issues
      let remoteVersion: string;
      try {
        console.log(
          `[UPGRADE] 🌐 Starting FRESH remote fetch from: ${featureDescUrl}`
        );
        console.log(`[UPGRADE] Timestamp: ${new Date().toISOString()}`);
        console.log(`[UPGRADE] Navigator online: ${navigator.onLine}`);

        const fetchStartTime = Date.now();
        console.log(`[UPGRADE] 🚀 Fetching fresh data WITHOUT caching...`);

        // Fetch without caching to avoid descriptor/bundle sync issues
        const remoteResource = await lastValueFrom(
          this.cacheService.getResourceWithoutCaching(featureDescUrl)
        );

        const fetchDuration = Date.now() - fetchStartTime;
        console.log(`[UPGRADE] ✅ Fresh fetch completed in ${fetchDuration}ms`);

        if (!remoteResource) {
          console.warn(
            `[UPGRADE] ❌ Failed to fetch remote version - null response`
          );
          console.log(
            `[UPGRADE] 🔚 RETURNING: Null response (duration: ${
              Date.now() - functionStartTime
            }ms)`
          );
          return { hasUpgrade: false };
        }

        console.log(`[UPGRADE] Resource fetched:`, remoteResource);
        console.log(`[UPGRADE] Content type: ${remoteResource.contentType}`);

        const remoteData = remoteResource.data;
        console.log(`[UPGRADE] ✅ Remote data retrieved successfully`);

        remoteVersion = remoteData?.version;
        console.log(
          `[UPGRADE] 📌 Remote version for ${featureName}: ${remoteVersion}`
        );
        console.log(`[UPGRADE] Full remote data:`, remoteData);
      } catch (err) {
        console.error(`[UPGRADE] ❌ EXCEPTION during remote fetch:`, err);
        console.error(`[UPGRADE] Error type: ${err?.constructor?.name}`);
        console.error(`[UPGRADE] Error message: ${err?.message}`);
        console.error(`[UPGRADE] Error stack:`, err?.stack);
        console.error(`[UPGRADE] Error details:`, {
          message: err?.message,
          stack: err?.stack,
          name: err?.name,
          toString: err?.toString(),
        });

        // Check if it's a network error
        if (err instanceof TypeError && err.message.includes("network")) {
          console.error(`[UPGRADE] 🔴 NETWORK ERROR detected`);
        }

        // Check if it's a timeout
        if (err?.name === "AbortError" || err?.message?.includes("timeout")) {
          console.error(`[UPGRADE] ⏱️ TIMEOUT ERROR detected`);
        }

        console.log(
          `[UPGRADE] 🔚 RETURNING: Exception during fetch (duration: ${
            Date.now() - functionStartTime
          }ms)`
        );
        return { hasUpgrade: false };
      }

      if (!remoteVersion) {
        console.log(`[UPGRADE] No version field in remote feature-description`);
        console.log(
          `[UPGRADE] 🔚 RETURNING: No version in remote data (duration: ${
            Date.now() - functionStartTime
          }ms)`
        );
        return { hasUpgrade: false };
      }

      // Compare versions
      // If running version has no version field (old feature) but remote has one, offer upgrade
      if (!currentVersion && remoteVersion) {
        console.log(
          `[UPGRADE] ✨ Running version has no version field (old feature), but remote has v${remoteVersion}`
        );
        console.log(
          `[UPGRADE] 🔚 RETURNING: Upgrade available! (duration: ${
            Date.now() - functionStartTime
          }ms)`
        );
        return {
          hasUpgrade: true,
          data: {
            featureName: featureName,
            currentVersion: currentVersion || "unknown",
            newVersion: remoteVersion,
            baseUrl: trimmedBaseUrl,
          },
        };
      }

      // Normal version comparison
      if (currentVersion !== remoteVersion) {
        console.log(
          `[UPGRADE] ✨ Version mismatch for ${featureName}: current=${currentVersion}, remote=${remoteVersion}`
        );

        console.log(
          `[UPGRADE] 🔚 RETURNING: Upgrade available! (duration: ${
            Date.now() - functionStartTime
          }ms)`
        );
        return {
          hasUpgrade: true,
          data: {
            featureName: featureName,
            currentVersion: currentVersion,
            newVersion: remoteVersion,
            baseUrl: trimmedBaseUrl,
          },
        };
      }

      console.log(`[UPGRADE] ✓ ${featureName} is up to date`);
      console.log(
        `[UPGRADE] 🔚 RETURNING: Up to date (duration: ${
          Date.now() - functionStartTime
        }ms)`
      );
      return { hasUpgrade: false };
    } catch (err) {
      console.error(
        `[UPGRADE] ❌ OUTER CATCH: Error checking for feature upgrade:`,
        err
      );
      console.error(`[UPGRADE] Outer catch error details:`, {
        message: err?.message,
        stack: err?.stack,
        name: err?.name,
      });
      console.log(
        `[UPGRADE] 🔚 RETURNING: Outer exception (duration: ${
          Date.now() - functionStartTime
        }ms)`
      );
      return { hasUpgrade: false };
    }
  }

  /**
   * Show upgrade notification popup and handle user response
   */
  public async promptForUpgrade(
    upgradeData: FeatureUpgradeData
  ): Promise<boolean> {
    const result = await this.upgradeModalService.show({
      featureName: upgradeData.featureName,
      currentVersion: upgradeData.currentVersion,
      newVersion: upgradeData.newVersion,
      baseUrl: upgradeData.baseUrl,
    });

    if (result?.action === "accept" && result?.success) {
      // Upgrade was successful (handled by the component)
      return true;
    } else {
      // User dismissed or closed modal - will be prompted again next time
      return false;
    }
  }

  /**
   * Clear running version from memory to force reload from updated cache
   */
  public async upgradeFeature(baseUrl: string): Promise<void> {
    const upgradeStartTime = Date.now();
    console.log(
      `[UPGRADE] ═══════════════════════════════════════════════════════`
    );
    console.log(`[UPGRADE] 🚀 UPGRADE STARTED at ${new Date().toISOString()}`);
    console.log(`[UPGRADE] Base URL: ${baseUrl}`);

    try {
      const trimmedBaseUrl = baseUrl.trim();
      const featureDescUrl = `${trimmedBaseUrl}feature-description.json`;
      console.log(`[UPGRADE] Trimmed base URL: ${trimmedBaseUrl}`);
      console.log(`[UPGRADE] Feature descriptor URL: ${featureDescUrl}`);

      // Get the featureId from the feature service to clear persistent containers
      console.log(`[UPGRADE] 🔍 Looking up featureId by baseUrl...`);
      console.log(
        `[UPGRADE] Looking up feature with baseUrl: "${trimmedBaseUrl}"`
      );

      const featureInfo =
        await this.featureService.getFeatureDescriptionByBaseUrl(
          trimmedBaseUrl
        );
      const featureId = featureInfo?.feature_id;
      console.log(`[UPGRADE] Feature ID: ${featureId || "NOT FOUND"}`);
      console.log(`[UPGRADE] Feature info:`, featureInfo);

      // Step 1: Clear the running version and feature from memory
      console.log(
        `[UPGRADE] ─────────────────────────────────────────────────────────`
      );
      console.log("[UPGRADE] 📦 STEP 1: Clearing feature from memory...");

      // Clear running version
      console.log(
        `[UPGRADE] Checking runningVersions map for: "${trimmedBaseUrl}"`
      );
      console.log(
        `[UPGRADE] Current runningVersions map:`,
        Array.from(ReactNativeBundleComponent.runningVersions.entries())
      );

      if (ReactNativeBundleComponent.runningVersions.has(trimmedBaseUrl)) {
        const version =
          ReactNativeBundleComponent.runningVersions.get(trimmedBaseUrl);
        ReactNativeBundleComponent.runningVersions.delete(trimmedBaseUrl);
        console.log(
          `[UPGRADE] ✅ Removed running version ${version} for ${trimmedBaseUrl}`
        );
      } else {
        console.log(
          `[UPGRADE] ⚠️ No running version found for ${trimmedBaseUrl}`
        );
      }

      // Clear persistent container, loaded features, and script tags
      if (featureId) {
        console.log(
          `[UPGRADE] Calling clearFeatureFromMemory for featureId: ${featureId}`
        );
        ReactNativeBundleComponent.clearFeatureFromMemory(featureId);
        console.log(`[UPGRADE] ✅ clearFeatureFromMemory completed`);
      } else {
        console.warn(
          `[UPGRADE] ⚠️ Could not get featureId for ${trimmedBaseUrl}, skipping memory cleanup`
        );
        console.warn(
          `[UPGRADE] This means persistent containers and scripts may not be cleared!`
        );
      }

      // Step 2: Clear ALL cached resources for this feature (descriptor + bundle files)
      console.log(
        `[UPGRADE] ─────────────────────────────────────────────────────────`
      );
      console.log("[UPGRADE] 🗑️  STEP 2: Clearing all cached resources...");
      const clearStartTime = Date.now();
      await this.cacheService.clearResourcesByPrefix(trimmedBaseUrl);
      const clearDuration = Date.now() - clearStartTime;
      console.log(
        `[UPGRADE] ✅ Cleared all cached resources in ${clearDuration}ms`
      );

      // Step 3: Pre-download the new feature bundle so it's ready when user navigates back
      console.log(
        `[UPGRADE] ─────────────────────────────────────────────────────────`
      );
      console.log("[UPGRADE] 📥 STEP 3: Downloading new feature bundle...");
      const downloadStartTime = Date.now();

      // Download feature descriptor
      console.log(
        `[UPGRADE] 📄 Downloading feature-description.json from ${featureDescUrl}...`
      );
      const descStartTime = Date.now();
      const featureDesc = await lastValueFrom(
        this.cacheService.getResource(featureDescUrl, true)
      );
      const descDuration = Date.now() - descStartTime;
      console.log(
        `[UPGRADE] ✅ Downloaded descriptor in ${descDuration}ms, version: ${featureDesc?.data?.version}`
      );

      // Download index.html
      const indexUrl = `${trimmedBaseUrl}index.html`;
      console.log(`[UPGRADE] 📄 Downloading index.html from ${indexUrl}...`);
      const indexStartTime = Date.now();
      await lastValueFrom(
        this.cacheService.getResourceAndOnlyCacheIfNative(indexUrl)
      );
      const indexDuration = Date.now() - indexStartTime;
      console.log(`[UPGRADE] ✅ Downloaded index.html in ${indexDuration}ms`);

      // Download main bundle (bundle.js)
      const bundleUrl = `${trimmedBaseUrl}bundle.js`;
      console.log(`[UPGRADE] 📄 Downloading bundle.js from ${bundleUrl}...`);
      const bundleStartTime = Date.now();
      await lastValueFrom(
        this.cacheService.getResourceAndOnlyCacheIfNative(bundleUrl)
      );
      const bundleDuration = Date.now() - bundleStartTime;
      console.log(`[UPGRADE] ✅ Downloaded bundle.js in ${bundleDuration}ms`);

      const totalDownloadDuration = Date.now() - downloadStartTime;
      console.log(
        `[UPGRADE] ✅ All feature files downloaded in ${totalDownloadDuration}ms total`
      );

      // Step 4: Navigate to home - feature is now ready in cache
      console.log(
        `[UPGRADE] ─────────────────────────────────────────────────────────`
      );
      console.log("[UPGRADE] 🏠 STEP 4: Navigating to home...");
      await this.router.navigate(["/home"]);
      console.log("[UPGRADE] ✅ Navigation complete");

      const totalUpgradeDuration = Date.now() - upgradeStartTime;
      console.log(
        `[UPGRADE] ═══════════════════════════════════════════════════════`
      );
      console.log(`[UPGRADE] 🎉 UPGRADE COMPLETE in ${totalUpgradeDuration}ms`);
      console.log(`[UPGRADE] Feature: ${baseUrl}`);
      console.log(`[UPGRADE] New version: ${featureDesc?.data?.version}`);
      console.log(
        `[UPGRADE] ═══════════════════════════════════════════════════════`
      );
    } catch (err) {
      const errorDuration = Date.now() - upgradeStartTime;
      console.error(
        `[UPGRADE] ═══════════════════════════════════════════════════════`
      );
      console.error(`[UPGRADE] ❌ UPGRADE FAILED after ${errorDuration}ms`);
      console.error(`[UPGRADE] Feature: ${baseUrl}`);
      console.error(`[UPGRADE] Error:`, err);
      console.error(
        `[UPGRADE] ═══════════════════════════════════════════════════════`
      );
      throw err;
    }
  }

  /**
   * Check all features for upgrades and prompt user if any are found
   */
  public async checkAllFeaturesForUpgrades(): Promise<void> {
    console.log("[UPGRADE] checkAllFeaturesForUpgrades() called");
    try {
      const features = await this.featureService.getFeatureDescriptions();
      console.log(
        `[UPGRADE] Checking ${features?.length || 0} features for upgrades`
      );

      for (const feature of features) {
        const baseUrl = feature.baseUrl || feature.feature_url;
        if (!baseUrl) continue;

        const { hasUpgrade, data } = await this.checkForFeatureUpgrade(baseUrl);

        if (hasUpgrade && data) {
          // Log the version details
          console.log(
            `[UPGRADE] 🔔 Upgrade available for ${data.featureName}:`
          );
          console.log(`[UPGRADE]   Current version: ${data.currentVersion}`);
          console.log(`[UPGRADE]   New version: ${data.newVersion}`);

          // Show upgrade prompt (one at a time)
          const upgraded = await this.promptForUpgrade(data);
          if (upgraded) {
            // If user upgraded, reload the page to reflect changes
            console.log("Feature upgraded, reloading page...");
            window.location.reload();
            return; // Stop checking after first upgrade
          }
        }
      }
    } catch (err) {
      console.error("Error checking features for upgrades:", err);
    }
  }
}
